import { memo, useMemo } from "react";
import { CellContext } from "@tanstack/react-table";
import type { ViewTableFeatures } from "@design-system/ViewTable/core/table";
import { ViewTableCell } from "@design-system/ViewTable/ViewTableCell";
import { UserAvatar } from "@components/avatar/UserAvatar";
import { AvatarTooltip } from "@components/avatar/AvatarTooltip";
import { AvatarGroup } from "@design-system/Avatar/AvatarGroup";
import { HStack } from "@design-system/HStack/HStack";
import { useCurrentWorkspaceMembers } from "@api/workspaceMember.api";
import type { WorkspaceMember } from "@common/types/workspace";
import { FieldReviewDetailsRow } from "../fieldReviewDetailsViewConfig";
import styles from "./FieldReviewCells.module.scss";

const getReviewerName = (member: WorkspaceMember) =>
    member.user.preferredName ?? member.user.name;

export const FieldReviewReviewerCell = memo(
    ({
        row,
    }: CellContext<ViewTableFeatures, FieldReviewDetailsRow, number[]>) => {
        const { reviewerIds } = row.original;
        const { data: allMembers = [] } = useCurrentWorkspaceMembers();

        const reviewers = useMemo(() => {
            const idSet = new Set(reviewerIds);
            return allMembers.filter((m) => idSet.has(m.user.id));
        }, [reviewerIds, allMembers]);

        if (reviewers.length === 0) {
            return <ViewTableCell />;
        }

        return (
            <ViewTableCell>
                <HStack gap={4} align="center">
                    <AvatarGroup
                        items={reviewers}
                        getKey={(member) => member.user.id}
                        getLabel={getReviewerName}
                        renderAvatar={(member) => (
                            <AvatarTooltip content={getReviewerName(member)}>
                                <UserAvatar
                                    user={{
                                        name: getReviewerName(member),
                                        avatar: member.user.avatar,
                                        iconColor: member.user.iconColor,
                                        email: member.user.email,
                                    }}
                                />
                            </AvatarTooltip>
                        )}
                        max={3}
                        size="xs"
                    />
                    {reviewers.length === 1 && (
                        <span className={styles.reviewerName}>
                            {getReviewerName(reviewers[0])}
                        </span>
                    )}
                </HStack>
            </ViewTableCell>
        );
    },
);

FieldReviewReviewerCell.displayName = "FieldReviewReviewerCell";
